import { useCallback, useEffect, useMemo, useState } from 'react';
import EChartsReactCore from 'echarts-for-react/lib/core'

import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	IconButton,
	Paper,
	Stack,
	Switch,
	Tooltip,
	Typography
} from "@mui/material"
import {
	ContentCopy as CsvCopyIcon
 } from '@mui/icons-material';
 import { CloseIcon, DownloadIcon } from "shared/icons";
import { clipImage, downloadImage } from 'components/download/image';
import { NumericInput } from 'components/controls/numbericInput';

type GraphImageDialogProps = {
	title: string
	echartsInstance: EChartsReactCore
	onClose: () => void
}
export const GraphImageDialog = ({
	title,
	echartsInstance,
	onClose
}: GraphImageDialogProps) => {
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resize, setResize] = useState<boolean>(false)
  const [imageWidth, setImageWidth] = useState<string>('1024')
  const [imageHeight, setImageHeight] = useState<string>('768')

	const [resizeParam, setResizeParam] = useState<{
		width: number,
		height: number
	} | null>(null)

	useEffect(
    () => {
			const timer = setTimeout(() => {
				if (resize) {
					const width = imageWidth.trim() === '' ? 0 : parseInt(imageWidth)
					const height = imageHeight.trim() === '' ? 0 : parseInt(imageHeight)
					setResizeParam((width <= 100 || height <= 100) ? null : { width, height })
				} else {
					setResizeParam(null)
				}
			}, 500)
			return () => {
				clearTimeout(timer)
			}
    },
    [resize, imageWidth, imageHeight]
  )
	const imageData = useMemo(
		() => {
			try {
				const instance = echartsInstance.getEchartsInstance()
				if (!instance) return null

				if (resizeParam) {
					const preStyle = {
						width: instance.getWidth(),
						height: instance.getHeight()
					}
					instance.resize({ ...resizeParam, silent: true })
						
					const imageData = instance.getDataURL({
						type: 'png',
						pixelRatio: window.devicePixelRatio * 1.5,
						backgroundColor: '#fff'
					})
					instance.resize({ ...preStyle, silent: true })
					return imageData
				} else {
					return instance.getDataURL({
						type: 'png',
						backgroundColor: '#fff'
					})
				}
			} catch (e) {
				console.error(e)
				return null
			}
		},
		[echartsInstance, resizeParam]
	)
	const onImageCopy = useCallback(
		async () => {
			if (!imageData) return
			try {
				await clipImage(imageData)
				onClose()
			} catch (e) {
				console.error('コピーに失敗しました', e)
				setErrorMessage('コピーに失敗しました')
			}
		},
		[imageData, onClose]
	)
	const onImageDownload = useCallback(
		async () => {
			if (!imageData) return
			try {
				downloadImage(imageData, title)
				onClose()
			} catch (e) {
				console.error('ダウンロードに失敗しました', e)
				setErrorMessage('ダウンロードに失敗しました')
			}
		},
		[imageData, title, onClose]
	)
	return (
    <Dialog
			open
			maxWidth="lg"
			scroll="paper"
			onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        onClose()
      }}
    >
      <DialogTitle>
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography
						variant="body1"
						textAlign="center"
						color="text.primary"
						flexGrow={1}
						overflow="hidden"
						textOverflow="ellipsis"
						whiteSpace="nowrap"
					>
						{title}
					</Typography>
		      <Tooltip title="閉じる">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					padding: 0,
					overflow: "hidden"
				}}
			>
				<Stack direction="row">
					<Box
						flexGrow={1}
						overflow="auto"
						maxHeight="70vh"
						minHeight="200px"
						maxWidth="50vw"
						minWidth="400px"
						padding={1}
						sx={{
							backgroundColor: "ghostwhite",
							textAlign: "center"
						}}
					>
						{imageData ? (
							<Paper square elevation={1}>
								<img
									src={imageData}
									alt={title}
								/>
							</Paper>
						) : (
							<Alert severity="info">画像が描画できません</Alert>
						)}
					</Box>
					<Paper square>	
						<FormGroup>
							<Stack direction='column' padding={1}>
								<FormControl>
									<FormControlLabel
										control={
											<Switch
												checked={resize}
												onChange={(_, checked) => setResize(checked)}
												size="small"
											/>
										}
										sx={{
											margin: 0
										}}
										label="サイズ指定"
									/>
								</FormControl>

								{ resize && (
									<Stack direction='row' alignItems='center' spacing={1} padding={1} marginTop={1}>
										<NumericInput
											state={imageWidth}
											setState={setImageWidth}
											label="幅"
											style={{ width: '5em' }}
										/>
										<NumericInput
											state={imageHeight}
											setState={setImageHeight}
											label="高さ"
											style={{ width: '5em' }}
										/>
									</Stack>
								) }
							</Stack>
						</FormGroup>
					</Paper>
				</Stack>
				
				{errorMessage && (
					<Alert severity="error">{errorMessage}</Alert>
				)}

			</DialogContent>

			<DialogActions
				sx={{
					justifyContent: 'flex-end',
					padding: 2
				}}
			>
				<Stack direction="row" spacing={2}>
					<Button
						variant="contained"
						onClick={onImageCopy}
						startIcon={<CsvCopyIcon/>}
					>
						コピー
					</Button>
					<Button
						variant="contained"
						onClick={onImageDownload}
						startIcon={<DownloadIcon/>}
					>
						ダウンロード
					</Button>
					<Button
						variant="text"
						onClick={onClose}
						startIcon={<CloseIcon/>}
					>
						閉じる
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	)
}